import React, { useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import { Controller, useForm } from "react-hook-form";
import { DropdownList } from "react-widgets";
import ErrorMessage from "../../../components/text/ErrorMessage";
import {
  Customer,
  Phone,
  EmailAddress,
  AgeLimit,
  LeadCustomerDetails,
  Designation,
  Influencer,
  Department,
  Seniority,
} from "../../../core/api/models";
import CustomerPhone from "../../../components/customer-phone";
import CustomerEmail from "../../../components/customer-email";
import DateInput from "../../../components/form-group/date-input";
import { UploadUserImage } from "../../../components/upload";
import Countries from "../../../utils/countries.json";
import States from "../../../utils/states.json";
import title from "../../../utils/title.json";
import "react-widgets/dist/css/react-widgets.css";
import api from "../../../api";
import {
  CheckPhoneNumberEmpty,
  ValidateAlphabets,
  ValidateEmail,
  Validate965DigitPhoneNumber,
  ValidateGST,
  ValidatePhoneNumberRegEx,
  ValidateAlphaNumeric,
} from "../../../utils/Validatation";
import moment from "moment";
import { useDataLoader } from "react-remote-data-hooks";
import PhoneInput from "../../../components/phone-input";
import {
  addressType,
  contactStatus,
  customerContactType,
  isDirectorOfCompany,
  isVerified,
} from "../../../core/api/models/customer";

const gridColumnStyleDetails = {
  1: "col-12",
  2: "col-md-6 col-12",
  3: "col-md-4 col-12",
};

const SearchCustomer: React.FunctionComponent<{
  error?: string;
  onCreateCustomer: (data: Customer) => void;
  setStage?: (stage: string) => void;
  customerDetails?: Customer;
  phones?: Phone[];
  emails?: EmailAddress[];
  isContact?: boolean;
  designationId?: string | null;
  isCompany360?: boolean;
  parentCompanyCustomer?: Customer;
  onClose?: () => void;
  onCloseStageName?: string;
  companyId?: string;
  isCompanyCustomer?: boolean | undefined;
  isContactlessCustomer?: boolean | undefined;
  gridColumns?: 1 | 2 | 3;
}> = (props) => {
  const gridColumnStyle: string = !!props.gridColumns
    ? gridColumnStyleDetails[props.gridColumns]
    : "col-12";

  const [isEmailEnable, setIsEmailEnable] = useState<boolean>(true);
  const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
  const [selectedPhone, setSelectedPhone] = useState<Phone[] | undefined>(props.phones);
  const [selectedEmail, setSelectedEmail] = useState<EmailAddress[] | undefined>(props.emails);
  const [isEmailOrPhone, setIsEmailOrPhone] = useState<boolean>(false);

  const { data: influencers } = useDataLoader<Influencer[]>(() =>
    api.influencer.loadAllInfluencer()
  );

  const { data: designations } = useDataLoader<Designation[]>(() =>
    api.designation.loadAllDesignation()
  );
  const { data: departmentDetails } = useDataLoader<Department[]>(() =>
    api.department.loadAllDepartments()
  );
  const { data: seniorityDetails } = useDataLoader<Seniority[]>(() =>
    api.seniority.loadAllSeniority()
  );

  const { control, handleSubmit, errors, clearErrors, watch, setValue } = useForm<Customer>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });
  React.useEffect(() => {
    if (watch("dob")) {
      const year = moment().diff(watch("dob"), "years", true);
      const ageRange = AgeLimit?.find((a) => a.from <= year && a.to >= year);
      setValue("ageRange", ageRange?.name);
    }
  }, [watch("dob")]);
  const [districtList, setDistrictList] = useState([]);

  const statesList = watch("country") === "India" || !watch("country") ? States : [];

  const onSubmit = async (data?: Customer) => {
    const hasEmailOrPhone = () => {
      if (!isEmailEnable && selectedPhone && selectedPhone?.every((x) => x.phone === "")) {
        setIsEmailOrPhone(true);
        return false;
      } else if (!selectedPhone && selectedEmail && selectedEmail?.every((sm) => sm.email === "")) {
        setIsEmailOrPhone(true);
        return false;
      } else if (
        selectedPhone &&
        selectedEmail &&
        selectedPhone?.every((x) => x.phone === x.dialCode || x.phone === "") &&
        selectedEmail?.every((sm) => sm.email === "")
      ) {
        setIsEmailOrPhone(true);
        return false;
      } else if (!selectedPhone && !selectedEmail) {
        setIsEmailOrPhone(true);
        return false;
      } else {
        setIsEmailOrPhone(false);
        return true;
      }
    };

    if (!!props?.isContactlessCustomer ? true : hasEmailOrPhone()) {
      setIsSubmiting(true);
      const updateData: Customer = {
        isVerified: data?.isVerified,
        prefix: data?.prefix,
        firstName: data?.firstName,
        lastName: data?.lastName,
        ageRange: data?.ageRange,
        dob: data?.dob,
        phones: selectedPhone?.filter((x) => CheckPhoneNumberEmpty(x.phone)),
        emails: selectedEmail?.filter((x) => ValidateEmail(x.email)),
        photo: data?.photo,
        avatar: data?.avatar,
        dinNumber: data?.dinNumber,
        linkedinProfile: data?.linkedinProfile,
        facebookProfile: data?.facebookProfile,
        panNumber: data?.panNumber,
        gstNumber: data?.gstNumber,
        companyId: props.companyId,
        customerContactType: data?.customerContactType,
        assistantName: data?.assistantName,
        assistantNumber: data?.assistantNumber,
        assistantDesignation: data?.assistantDesignation,
        seniority: data?.seniority,
        department: data?.department,
        associationStartDate: data?.associationStartDate,
        associationEndDate: data?.associationEndDate,
        isDirectorOfCompany: data?.isDirectorOfCompany,
        accountManager: data?.accountManager,
        remarks: data?.remarks,
      };
      if (props.isContact && props.parentCompanyCustomer) {
        const {
          _id,
          prefix,
          firstName,
          lastName,
          middleName,
          fullName,
          phone,
          avatar,
        } = props.parentCompanyCustomer;
        const parentCustomerDetails: LeadCustomerDetails = {
          _id: _id!,
          prefix,
          firstName: firstName!,
          lastName,
          middleName,
          fullName,
          phone,
          email: props.parentCompanyCustomer.primaryEmail,
          magicNumber: props.parentCompanyCustomer.identifiers?.magicNumber,
          avatar,
        };
        updateData.parentCompanyCustomer = parentCustomerDetails;
      }
      updateData.influencerId = data?.influencerId;
      updateData.designationId = data?.designationId;
      updateData.divisionId = data?.divisionId;

      const hasAddressDetails = [
        data?.addressType,
        data?.address,
        data?.country,
        data?.state,
        data?.district,
        data?.city,
        data?.postalCode,
        data?.contactStatus,
      ].every((value) => !value);

      if (!hasAddressDetails) {
        updateData.addresses = [
          {
            addressType: data?.addressType,
            country: data?.country,
            state: data?.state,
            district: data?.district,
            city: data?.city,
            address: data?.address,
            postalCode: data?.postalCode,
            isPrimary: false,
            contactStatus: data?.contactStatus,
          },
        ];
      }
      try {
        const response = await api.customer.createCustomer(updateData);
        props.onCreateCustomer(response);
        toastr.success("Success", "Customer added successfully");
      } catch (error) {
        toastr.error("Error", error?.response?.data?.message);
      }
      setIsSubmiting(false);
    }
  };

  useEffect(() => {
    if (!isEmailEnable) {
      onChangeEmail([]);
    }
  }, [isEmailEnable]);

  const onChangePhone = (phoneNumber: Phone[]) => {
    setSelectedPhone(phoneNumber);
    setIsEmailOrPhone(false);
    clearErrors("phones");
  };

  const onChangeEmail = (emailAdd: EmailAddress[]) => {
    setSelectedEmail(emailAdd);
    setIsEmailOrPhone(false);
    clearErrors("emails");
    setValue("emails", emailAdd);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="p-4 ">
        <div className="row">
          <div className="form-group d-flex flex-wrap align-items-center col-12">
            <Controller
              control={control}
              name="photo"
              render={({ onChange, value, name }) => (
                <UploadUserImage id={"photo"} title={"Add Photo"} onChange={onChange} />
              )}
            />
          </div>
          <div className={`form-group font-weight-semibold mb-4  ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Contact Type
            </label>
            <Controller
              control={control}
              rules={{ required: false }}
              name="isVerified"
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={isVerified}
                  textField={"name"}
                  valueField={"value"}
                  placeholder={"Select contact Type"}
                  onChange={(val) => {
                    onChange(val.value);
                  }}
                />
              )}
            />
            {errors.isVerified && <ErrorMessage>Contact Type is required</ErrorMessage>}
          </div>
        </div>

        <div className="row">
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Title<span className="text-danger">*</span>
            </label>
            <Controller
              control={control}
              rules={{ required: true }}
              name="prefix"
              // defaultValue={props.customerDetails?.prefix}
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={title}
                  textField={"name"}
                  valueField={"name"}
                  placeholder={"Select prefix"}
                  onChange={(val) => {
                    onChange(val.name);
                  }}
                />
              )}
            />
            {errors.prefix && <ErrorMessage>Prefix is required</ErrorMessage>}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              First Name<span className="text-danger">*</span>
            </label>

            <Controller
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  return !!ValidateAlphabets(value);
                },
                maxLength: 25,
              }}
              name="firstName"
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="First Name"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="firstName"
                />
              )}
            />
            {errors.firstName && errors.firstName.type === "required" && (
              <ErrorMessage>First Name is required</ErrorMessage>
            )}
            {errors.firstName && errors.firstName.type === "maxLength" && (
              <ErrorMessage>First name should contains maximum 25 characters</ErrorMessage>
            )}
            {errors.firstName && errors.firstName.type === "validate" && (
              <ErrorMessage>Invalid First Name</ErrorMessage>
            )}
          </div>

          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Last Name
            </label>
            <Controller
              control={control}
              name="lastName"
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="Last Name"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="lastName"
                />
              )}
            />
            {errors.lastName && errors.lastName.type === "required" && (
              <ErrorMessage>Last Name is required</ErrorMessage>
            )}
            {errors.lastName && errors.lastName.type === "maxLength" && (
              <ErrorMessage>Last name should contains maximum 25 characters</ErrorMessage>
            )}
            {errors.lastName && errors.lastName.type === "validate" && (
              <ErrorMessage>Invalid Last Name</ErrorMessage>
            )}
          </div>

          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase d-flex align-items-end">
              Email
              <div className="ml-auto switch switch--sm">
                <input
                  type="checkbox"
                  name=""
                  onChange={(val) => {
                    setIsEmailEnable(!isEmailEnable);
                  }}
                  id="emailSwitch"
                  checked={isEmailEnable}
                />
                <label htmlFor="emailSwitch">
                  <div className="switch-handle mr-2"></div>
                </label>
              </div>
            </label>
            <Controller
              control={control}
              rules={{
                required: !!props?.isContactlessCustomer ? false : isEmailEnable,
                validate: (value: EmailAddress[]) => {
                  if (!!props?.isContactlessCustomer) return true;
                  if (!isEmailEnable) {
                    return undefined;
                  }
                  if (value.length && value?.every((x) => ValidateEmail(x.email))) {
                    return undefined;
                  }
                  return "Valid email is required";
                },
              }}
              name="emails"
              defaultValue={props.emails}
              render={({ onChange, value, name }) => {
                return (
                  <CustomerEmail
                    key={`${isEmailEnable}`}
                    className="form-control p-0 rounded-sm font-weight-semibold"
                    placeholder="Email"
                    value={selectedEmail}
                    onChange={onChangeEmail}
                    disabled={!isEmailEnable}
                  />
                );
              }}
            />
            {errors.emails && (
              <div>
                <ErrorMessage>Valid email is required</ErrorMessage>
              </div>
            )}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Phone
            </label>
            <Controller
              control={control}
              rules={{
                validate: (value) => {
                  if (props?.isContactlessCustomer) return true;
                  if (
                    selectedPhone?.length &&
                    selectedPhone?.every(
                      (x) =>
                        x.phone === x.dialCode || x.phone === `+${x.dialCode}` || x.phone === ""
                    )
                  ) {
                    return true;
                  }
                  return !(
                    (
                      selectedPhone &&
                      selectedPhone?.filter((x) => Validate965DigitPhoneNumber(x.phone)).length !==
                      selectedPhone?.length
                    )
                  );
                },
              }}
              name="phones"
              defaultValue={props.phones}
              render={({ onChange, value, name }) => (
                <CustomerPhone
                  className="form-control p-0 rounded-sm font-weight-semibold"
                  placeholder="Phone"
                  value={selectedPhone}
                  onChange={(val) => {
                    onChangePhone(val);
                    onChange(val);
                  }}
                />
              )}
            />
            {errors.phones && (
              <div>
                <div>
                  {!selectedPhone ? (
                    <ErrorMessage>Valid Phone is required</ErrorMessage>
                  ) : (
                    <>
                      {selectedPhone?.filter(
                        (x) => !!(x.phone.startsWith("91") || x.phone.startsWith("+91"))
                      ).length > 0 ? (
                        <ErrorMessage>Phone number should be 10 digits</ErrorMessage>
                      ) : (
                        <ErrorMessage>Invalid Phone number</ErrorMessage>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              DIN Number
            </label>
            <Controller
              control={control}
              name="dinNumber"
              rules={{
                validate: (value) => {
                  if (!value) return undefined;
                  return !ValidateAlphaNumeric(value) ? "Invalid DIN Number" : undefined;
                },
                maxLength: 8,
                minLength: 8,
              }}
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="DIN Number"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="dinNumber"
                />
              )}
            />
            {errors.dinNumber && errors.dinNumber.type === "maxLength" && (
              <ErrorMessage>DIN Number cannot have more than 8 characters!</ErrorMessage>
            )}
            {errors.dinNumber && errors.dinNumber.type === "minLength" && (
              <ErrorMessage>DIN Number cannot have less than 8 characters!</ErrorMessage>
            )}
            {errors.dinNumber && errors.dinNumber.type === "validate" && (
              <ErrorMessage>Invalid DIN Number</ErrorMessage>
            )}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Contact Category
            </label>
            <Controller
              control={control}
              rules={{ required: false }}
              name="customerContactType"
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={customerContactType}
                  textField={"name"}
                  valueField={"value"}
                  placeholder={"Select contact category"}
                  onChange={(val) => {
                    onChange(val.value);
                  }}
                />
              )}
            />
            {errors.customerContactType && (
              <ErrorMessage>Contact Category is required</ErrorMessage>
            )}
          </div>

          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              LinkedIn Profile
            </label>

            <Controller
              control={control}
              name="linkedinProfile"
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="Enter linkedin profile"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="linkedinProfile"
                />
              )}
            />
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Facebook Profile
            </label>

            <Controller
              control={control}
              name="facebookProfile"
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="Enter facebook profile"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="facebookProfile"
                />
              )}
            />
          </div>

          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              PAN Number
            </label>

            <Controller
              control={control}
              rules={{
                validate: (value) => {
                  if (value) {
                    const panRegex = /[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
                    return panRegex.test(value);
                  }
                },
                maxLength: 10,
              }}
              name="panNumber"
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="Enter PAN Number"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="panNumber"
                />
              )}
            />

            {errors.panNumber && errors.panNumber.type === "maxLength" && (
              <ErrorMessage>PAN No cannot have more than 10 characters!</ErrorMessage>
            )}
            {errors.panNumber && errors.panNumber && (
              <ErrorMessage>Invalid PAN No</ErrorMessage>
            )}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              GST Number
            </label>

            <Controller
              control={control}
              rules={{
                validate: (value) => {
                  if (value && !ValidateGST(value)) {
                    return "Invalid GST";
                  }
                },
              }}
              name="gstNumber"
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="Enter GST Number"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="gstNumber"
                />
              )}
            />
            {errors.gstNumber?.message && (
              <ErrorMessage>{errors.gstNumber?.message}</ErrorMessage>
            )}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Date Of Birth
            </label>
            <Controller
              control={control}
              name="dob"
              render={({ onChange, value, name }) => (
                <DateInput
                  default={value}
                  placeholder={"Date Of Birth"}
                  onChange={onChange}
                  maxDate={new Date()}
                />
              )}
            />
            {errors.dob && <ErrorMessage>Date of birth is required</ErrorMessage>}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Age Limit
            </label>
            <Controller
              control={control}
              name="ageRange"
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={AgeLimit}
                  textField={"name"}
                  valueField={"name"}
                  placeholder={"Select Age Limit"}
                  onChange={(val) => {
                    onChange(val.name);
                  }}
                />
              )}
            />
            {errors.ageRange && <ErrorMessage>Age limit is required</ErrorMessage>}
          </div>

          {props?.isCompanyCustomer && (
            <>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Assistant Name
                </label>

                <Controller
                  control={control}
                  name="assistantName"
                  render={({ onChange, value, name }) => (
                    <input
                      type="text"
                      className="form-control rounded-sm font-weight-semibold"
                      placeholder="Enter Assistant Name"
                      name={name}
                      value={value}
                      onChange={onChange}
                      id="assistantName"
                    />
                  )}
                />
                {errors.assistantName && <ErrorMessage>Assistant Name is required</ErrorMessage>}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Assistant Number
                </label>

                <Controller
                  control={control}
                  rules={{
                    required: false,
                    validate: (value) =>
                      !ValidatePhoneNumberRegEx(value || "") && !!value
                        ? "Invalid phone"
                        : undefined,
                  }}
                  name="assistantNumber"
                  render={({ onChange, value, name }) => (
                    <>
                      <PhoneInput
                        country={"in"}
                        value={`${value}`}
                        onChange={(phone, country) => onChange(`${phone}`)}
                      />
                    </>
                  )}
                />

                {errors?.assistantNumber?.message && (
                  <ErrorMessage>{errors.assistantNumber?.message}</ErrorMessage>
                )}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Assistant Designation
                </label>

                <Controller
                  control={control}
                  // rules={{ required: true }}
                  name="assistantDesignation"
                  render={({ onChange, value, name }) => (
                    <input
                      type="text"
                      className="form-control rounded-sm font-weight-semibold"
                      placeholder="Enter Assistant Designation"
                      name={name}
                      value={value}
                      onChange={onChange}
                      id="assistantDesignation"
                    />
                  )}
                />
                {errors.assistantDesignation && (
                  <ErrorMessage>Assistant Designation is required</ErrorMessage>
                )}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Seniority
                </label>
                <Controller
                  control={control}
                  rules={{ required: false }}
                  name="seniority"
                  // defaultValue={props.customerDetails?.seniority}
                  render={({ onChange, value, name }) => (
                    <DropdownList
                      value={value}
                      defaultValue={value}
                      data={seniorityDetails}
                      textField={"title"}
                      valueField={"_id"}
                      placeholder={"Select Seniority"}
                      onChange={(val) => {
                        onChange(val._id);
                      }}
                    />
                  )}
                />
                {errors.seniority && <ErrorMessage>Seniority is required</ErrorMessage>}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Department
                </label>

                <Controller
                  control={control}
                  // rules={{ required: true }}
                  name="department"
                  render={({ onChange, value, name }) => (
                    <DropdownList
                      value={value}
                      defaultValue={value}
                      data={departmentDetails}
                      textField={"title"}
                      valueField={"_id"}
                      placeholder={"Enter Department"}
                      onChange={(val) => {
                        onChange(val._id);
                      }}
                    />
                  )}
                />
                {errors.department && <ErrorMessage>Department is required</ErrorMessage>}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Association Start Date
                </label>
                <Controller
                  control={control}
                  name="associationStartDate"
                  render={({ onChange, value, name }) => (
                    <DateInput
                      default={value}
                      placeholder={"Association Start Date"}
                      onChange={onChange}
                      {...(watch("associationEndDate") !== undefined
                        ? { maxDate: new Date(watch("associationEndDate") || "") }
                        : {})}
                    />
                  )}
                />
                {errors.associationStartDate && (
                  <ErrorMessage>Association Start Date is required</ErrorMessage>
                )}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Association End Date
                </label>
                <Controller
                  control={control}
                  name="associationEndDate"
                  render={({ onChange, value, name }) => (
                    <DateInput
                      default={value}
                      placeholder={"Association End Date"}
                      onChange={onChange}
                      {...(watch("associationStartDate") !== undefined
                        ? { minDate: new Date(watch("associationStartDate") || "") }
                        : {})}
                    />
                  )}
                />
                {errors.associationEndDate && (
                  <ErrorMessage>Association End Date is required</ErrorMessage>
                )}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Director With Company
                </label>
                <Controller
                  control={control}
                  rules={{ required: false }}
                  name="isDirectorOfCompany"
                  // defaultValue={props.customerDetails?.isDirectorOfCompany}
                  render={({ onChange, value, name }) => (
                    <DropdownList
                      value={value}
                      defaultValue={value}
                      data={isDirectorOfCompany}
                      textField={"name"}
                      valueField={"value"}
                      placeholder={"Select Director With Company"}
                      onChange={(val) => {
                        onChange(val.value);
                      }}
                    />
                  )}
                />
                {errors.isDirectorOfCompany && (
                  <ErrorMessage>Director With Company is required</ErrorMessage>
                )}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Account Manager
                </label>

                <Controller
                  control={control}
                  // rules={{ required: true }}
                  name="accountManager"
                  render={({ onChange, value, name }) => (
                    <input
                      type="text"
                      className="form-control rounded-sm font-weight-semibold"
                      placeholder="Enter Account Manager"
                      name={name}
                      value={value}
                      onChange={onChange}
                      id="accountManager"
                    />
                  )}
                />
                {errors.accountManager && <ErrorMessage>Account Manager is required</ErrorMessage>}
              </div>
            </>
          )}

          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Designation
              {props?.isContactlessCustomer && <span className="text-danger">*</span>}
            </label>
            <Controller
              control={control}
              rules={{ required: props?.isContactlessCustomer }}
              name="designationId"
              defaultValue={
                props?.isContactlessCustomer
                  ? props.designationId
                  : props?.customerDetails?.designation?._id
              }
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  filter="contains"
                  defaultValue={value}
                  data={designations?.map((key) => {
                    return {
                      _id: key._id,
                      name: key.name,
                    };
                  })}
                  textField={"name"}
                  valueField={"_id"}
                  placeholder={"Select Designation"}
                  onChange={(val) => {
                    onChange(val._id);
                  }}
                />
              )}
            />
            {errors.designationId && <ErrorMessage>Designation is required</ErrorMessage>}
          </div>

          {props.isContact && !props.isCompany360 && (
            <>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Influencer
                </label>
                <Controller
                  control={control}
                  name="influencerId"
                  defaultValue={props?.customerDetails?.influencer?._id}
                  render={({ onChange, value, name }) => (
                    <DropdownList
                      value={value}
                      defaultValue={value}
                      data={influencers?.map((key) => {
                        return {
                          _id: key._id,
                          name: key.name,
                        };
                      })}
                      textField={"name"}
                      valueField={"_id"}
                      placeholder={"Select Influencer"}
                      onChange={(val) => {
                        onChange(val._id);
                      }}
                    />
                  )}
                />
                {errors.influencerId && <ErrorMessage>Influencer is required</ErrorMessage>}
              </div>
              <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
                <label htmlFor="" className="font-sm text-uppercase">
                  Designation
                  {props?.isContactlessCustomer && <span className="text-danger">*</span>}
                </label>
                <Controller
                  control={control}
                  // rules={{ required: true }}
                  rules={{ required: props?.isContactlessCustomer }}
                  name="designationId"
                  // defaultValue={props?.customerDetails?.designation?._id}
                  defaultValue={
                    props?.isContactlessCustomer
                      ? props.designationId
                      : props?.customerDetails?.designation?._id
                  }
                  render={({ onChange, value, name }) => (
                    <DropdownList
                      value={value}
                      defaultValue={value}
                      filter={"contains"}
                      data={designations?.map((key) => {
                        return {
                          _id: key._id,
                          name: key.name,
                        };
                      })}
                      textField={"name"}
                      valueField={"_id"}
                      placeholder={"Select Designation"}
                      onChange={(val) => {
                        onChange(val._id);
                      }}
                    />
                  )}
                />
                {errors.designationId && <ErrorMessage>Designation is required</ErrorMessage>}
              </div>
            </>
          )}

          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Remarks
            </label>
            <Controller
              control={control}
              name="remarks"
              render={({ onChange, value, name }) => (
                <textarea
                  rows={3}
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="Remarks"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="remarks"
                />
              )}
            />
            {errors.remarks && <ErrorMessage>remarks is required</ErrorMessage>}
          </div>
        </div>

        <hr />
        <h5 className="text-uppercase font-weight-semibold mb-2">Address</h5>
        <div className="row">
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Address Type
            </label>
            <Controller
              control={control}
              name="addressType"
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={addressType}
                  textField={"name"}
                  valueField={"value"}
                  placeholder={"Select address Type"}
                  onChange={(val) => {
                    onChange(val.value);
                  }}
                />
              )}
            />
            {errors.addressType && <ErrorMessage>Address Type is required</ErrorMessage>}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Address
              {/* <span className="text-danger">*</span> */}
            </label>
            <Controller
              control={control}
              // rules={{ required: true }}
              name="address"
              render={({ onChange, value, name }) => (
                <textarea
                  rows={3}
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="Address"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="address"
                />
              )}
            />
            {errors.address && <ErrorMessage>Address is required</ErrorMessage>}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Country
              {/* <span className="text-danger">*</span> */}
            </label>

            <Controller
              control={control}
              // rules={{ required: true }}
              name="country"
              // defaultValue="India"
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={Countries}
                  textField={"name"}
                  valueField={"name"}
                  placeholder={"Select country"}
                  onChange={(val) => {
                    onChange(val.name);
                  }}
                  filter={"contains"}
                />
              )}
            />
            {errors.country && (
              <ErrorMessage>
                {errors.country.type === "required" ? "Country is required" : "Invalid Country"}
              </ErrorMessage>
            )}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              State
              {/* <span className="text-danger">*</span> */}
            </label>

            <Controller
              control={control}
              // rules={{ required: true }}
              name="state"
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={statesList}
                  textField={"state"}
                  valueField={"state"}
                  placeholder={"Select state"}
                  onChange={(val) => {
                    onChange(val.state);
                    setDistrictList(val.districts);
                  }}
                />
              )}
            />
            {errors.state && (
              <ErrorMessage>
                {errors.state.type === "required" ? "State is required" : "Invalid State"}
              </ErrorMessage>
            )}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              District
              {/* <span className="text-danger">*</span> */}
            </label>
            <Controller
              control={control}
              // rules={{ required: true }}
              name="district"
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={districtList?.map((d) => {
                    return {
                      name: d,
                    };
                  })}
                  textField={"name"}
                  valueField={"name"}
                  placeholder={"Select district"}
                  onChange={(val) => {
                    onChange(val.name);
                  }}
                />
              )}
            />
            {errors.district && (
              <ErrorMessage>
                {errors.district.type === "required" ? "District is required" : "Invalid District"}
              </ErrorMessage>
            )}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              City
              {/* <span className="text-danger">*</span> */}
            </label>

            <Controller
              control={control}
              // rules={{ required: true }}
              name="city"
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="City"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="city"
                />
              )}
            />
            {errors.city && (
              <ErrorMessage>
                {errors.city.type === "required" ? "City is required" : "Invalid City"}
              </ErrorMessage>
            )}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Zip code
            </label>
            <Controller
              control={control}
              // rules={{
              //   required: false,
              //   pattern: {
              //     value: /^[0-9]{6}$/,
              //     message: "Invalid Postal Code",
              //   },
              // }}
              name="postalCode"
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  placeholder="Postal Code"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="postalCode"
                />
              )}
            />
            {errors.postalCode && (
              <ErrorMessage>
                {errors.postalCode.type === "required"
                  ? "Postal Code is required"
                  : "Invalid Postal Code"}
              </ErrorMessage>
            )}
          </div>
          <div className={`form-group font-weight-semibold mb-4 ${gridColumnStyle}`}>
            <label htmlFor="" className="font-sm text-uppercase">
              Contact Status
            </label>
            <Controller
              control={control}
              // rules={{ required: false }}
              name="contactStatus"
              // defaultValue={props.customerDetails?.isActive}
              render={({ onChange, value, name }) => (
                <DropdownList
                  value={value}
                  defaultValue={value}
                  data={contactStatus}
                  textField={"name"}
                  valueField={"value"}
                  placeholder={"Select contact status"}
                  onChange={(val) => {
                    onChange(val.value);
                  }}
                />
              )}
            />
            {errors.contactStatus && <ErrorMessage>Contact Status is required</ErrorMessage>}
          </div>

        </div>

        {isEmailOrPhone ? (
          <ErrorMessage>At least one of email or phone is required</ErrorMessage>
        ) : (
          ""
        )}
        <div className="row align-items-center">
          <div className="form-group pt-2 col-md-4 col-12 ">
            <button
              disabled={isSubmiting}
              className="btn btn-primary btn-block font-normal font-weight-bold"
            >
              {!props.isContact ? "Create Customer" : "Add Contact"}
            </button>
          </div>
          {props.setStage && (
            <button
              className="btn btn-outline-dark btn-block font-normal font-weight-bold btn-goback text-gray-500 mb-2 px-0 col-md-4 col-12"
              type="button"
              onClick={() =>
                props.onClose
                  ? props.onClose()
                  : props.setStage?.(props.onCloseStageName || "fetch-customer")
              }
            >
              Go Back
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default SearchCustomer;
